import {
  BugReport as BugReportIcon,
  Chat as ChatIcon,
  Dashboard as DashboardIcon,
  Extension as ExtensionIcon,
  Group as GroupIcon,
  RestartAlt as RestartIcon,
  Schedule as ScheduleIcon,
  Settings as SettingsIcon,
  Today as TodayIcon,
} from "@mui/icons-material";
import { Alert, Divider, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Snackbar } from "@mui/material";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, useRoleAccess } from "../hooks";
import { useOnboarding } from "../hooks/useOnboarding";
import { reviewService } from "../services/ReviewService";
import { userProfileService } from "../services/UserProfileService";

export const DebugMenu: React.FC = () => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({
    open: false,
    message: "",
    severity: "success",
  });
  const navigate = useNavigate();
  // Get user authentication and role access
  const { user } = useAuth();
  const { canAccessDebugMenu, canAccessDebugEvents, canAccessDebugThreads, canAccessDebugAgents, canAccessDebugPlugins, loading } = useRoleAccess({ uid: user?.uid });
  // Get onboarding state for restart functionality
  const { resetOnboarding } = useOnboarding();

  // Don't render if user can't access debug menu or still loading
  if (loading || !canAccessDebugMenu) {
    return null;
  }

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNavigate = (path: string) => {
    navigate(path);
    handleClose();
  };

  const handleTriggerReview = async (reviewType: "weekly" | "daily") => {
    try {
      if (reviewType === "weekly") {
        await reviewService.triggerWeeklyReview();
        setSnackbar({
          open: true,
          message: "Weekly review triggered successfully!",
          severity: "success",
        });
      } else {
        await reviewService.triggerDailyReview();
        setSnackbar({
          open: true,
          message: "Daily review triggered successfully!",
          severity: "success",
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: `Failed to trigger ${reviewType} review: ${error instanceof Error ? error.message : "Unknown error"}`,
        severity: "error",
      });
    }
    handleClose();
  };

  const handleRestartOnboarding = async () => {
    try {
      // Reset the onboarding flag in the user profile
      await userProfileService.resetOnboarding();
      // Also reset the local onboarding state and refresh status
      await resetOnboarding();
      setSnackbar({
        open: true,
        message: "Onboarding restarted successfully!",
        severity: "success",
      });
      handleClose();
      // Force a page reload to ensure clean state
      window.location.href = "/";
    } catch (error) {
      console.error("Error restarting onboarding:", error);
      setSnackbar({
        open: true,
        message: "Failed to restart onboarding. Please try again.",
        severity: "error",
      });
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <>
      <IconButton
        color="inherit"
        onClick={handleClick}
        sx={{
          color: "white",
          "&:hover": {
            backgroundColor: "rgba(255, 255, 255, 0.1)",
          },
        }}
      >
        <BugReportIcon />
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        PaperProps={{
          sx: {
            minWidth: 200,
            mt: 1,
          },
        }}
      >
        {canAccessDebugEvents && (
          <MenuItem onClick={() => handleNavigate("/debug/events")}>
            <ListItemIcon>
              <DashboardIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Debug Events</ListItemText>
          </MenuItem>
        )}

        {canAccessDebugThreads && (
          <MenuItem onClick={() => handleNavigate("/debug/threads")}>
            <ListItemIcon>
              <BugReportIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Debug Chat</ListItemText>
          </MenuItem>
        )}

        {canAccessDebugAgents && (
          <MenuItem onClick={() => handleNavigate("/debug/agents")}>
            <ListItemIcon>
              <GroupIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Debug Agents</ListItemText>
          </MenuItem>
        )}

        {canAccessDebugPlugins && (
          <MenuItem onClick={() => handleNavigate("/debug/plugins")}>
            <ListItemIcon>
              <ExtensionIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Debug Plugins</ListItemText>
          </MenuItem>
        )}

        {canAccessDebugEvents && (
          <MenuItem onClick={() => handleNavigate("/debug/messages")}>
            <ListItemIcon>
              <ChatIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Message Viewer</ListItemText>
          </MenuItem>
        )}

        <MenuItem onClick={() => handleNavigate("/google-services")}>
          <ListItemIcon>
            <SettingsIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Google Services</ListItemText>
        </MenuItem>

        <Divider />

        <MenuItem onClick={handleRestartOnboarding}>
          <ListItemIcon>
            <RestartIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Restart Onboarding</ListItemText>
        </MenuItem>

        <MenuItem onClick={() => handleTriggerReview("daily")}>
          <ListItemIcon>
            <TodayIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Trigger Daily Review</ListItemText>
        </MenuItem>

        <MenuItem onClick={() => handleTriggerReview("weekly")}>
          <ListItemIcon>
            <ScheduleIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Trigger Weekly Review</ListItemText>
        </MenuItem>
      </Menu>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleSnackbarClose} anchorOrigin={{ vertical: "top", horizontal: "center" }}>
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};
